import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { unwrap } from "./client";
import { queryKeys } from "./hooks";

/**
 * The typed vocabulary a BG domain document's enum fields may name (#913).
 *
 * Every name here is aliased from the generated contract rather than declared,
 * exactly as `summaryTargets.ts` does it: the service owns this vocabulary and a
 * shape that drifts from the contract must stop compiling rather than stop
 * working.
 *
 * `BackgroundItemStatus` earns its alias twice over. It is the type of the
 * values this query returns AND the key type of `ITEM_STATUS_LABELS` below — so
 * a status the pinned engine gains or loses fails `tsc` here, in the one place
 * that would otherwise render a raw engine key to a clinician.
 */
export type BackgroundItemStatus =
  components["schemas"]["BackgroundItemStatus"];
export type BackgroundItemType = components["schemas"]["BackgroundItemType"];
export type BackgroundDomainVocabulary =
  components["schemas"]["BackgroundDomainVocabulary"];

/**
 * What each tracker status is CALLED on screen.
 *
 * The values are the engine's; the English is this product's, which is why the
 * service publishes a list of values and not label/value pairs — a wording
 * change here must not be a contract change. `Record<BackgroundItemStatus, …>`
 * is what makes the pairing total: adding a status to the service without a
 * label here does not compile.
 *
 * Each label says what the TRACKER means, not what the word means in English —
 * "Denied" is the tracker's word for an answer that says no, and a rule keying
 * on it is the source's own way of spelling "the patient reported a problem".
 */
export const ITEM_STATUS_LABELS: Record<BackgroundItemStatus, string> = {
  pending: "Pending — not yet answered",
  captured: "Captured — answered with enough detail",
  unknown: "Unknown — the patient does not know",
  denied: "Denied — the patient answered no",
  declined: "Declined — the patient would rather not answer",
  not_triggered: "Not triggered — no rule has activated it",
};

/** What each item type is called on screen, on the same rule. */
export const ITEM_TYPE_LABELS: Record<BackgroundItemType, string> = {
  required: "Required — always asked",
  conditional: "Conditional — asked only when a rule activates it",
};

/**
 * The service's own BG domain enum vocabulary.
 *
 * WHY IT IS FETCHED AND NOT A CONSTANT. `background_domains.py` derives the six
 * statuses from the PINNED TRACKER and a real-checkout test fails when the two
 * diverge. A browser that listed them itself would be a third copy, pinned by
 * nothing and free to offer an operator the write gate refuses — the class of
 * duplication `assetContent.ts` documents at length. The labels above stay here
 * because they are product wording, and `tsc` keeps them exhaustive.
 *
 * SAME CACHE DISCIPLINE AS THE SUMMARY TARGET CATALOG. The key names no asset,
 * so every open domain editor reads one cache entry, and this read describes the
 * SERVICE rather than any asset: it must never block or re-seed an editor that
 * already has content in hand.
 */
export function useBackgroundVocabularyQuery(): UseQueryResult<BackgroundDomainVocabulary> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.backgroundDomainVocabulary,
    async queryFn() {
      return unwrap(await client.GET("/v1/library/background-vocabulary", {}));
    },
  });
}
